import { Button } from "../button";
import { Icon } from "../icons";
import { useTranslation } from "../localization";
import { StatusIndicator } from "../status-indicator";

/** Props Interface */
export interface RecordingPanelProps {
	isPaused: boolean;
	/** Lifts the panel into a floating window; absent when that is not on offer. */
	onUndock?: () => void;
	/** The running time, already formatted as `HH:MM:SS`. */
	elapsed: string;
	/** What is left before the recording stops itself, formatted; absent until that is near. */
	remaining?: string;
	onPause: () => void;
	onResume: () => void;
	onStop: () => void;
}

/**
 * The floating surface of a running recording: what it is doing, for how long, and the two
 * controls that change it.
 *
 * Recording and paused are deliberately *not* symmetric. Believing a recording is paused while
 * it runs writes whatever happens next into a tutorial — in this product that means employee
 * names, attendances, HR rows. Believing it is paused while it records only wastes a take. So
 * recording is the loud state (a pulsing red dot) and paused is the quiet one: neutral colour,
 * no pulse, a frozen clock.
 *
 * Presentational on purpose: everything arrives as props so the panel can be rendered and
 * judged without a capture device.
 */
export function RecordingPanel({
	isPaused,
	onUndock,
	elapsed,
	remaining,
	onPause,
	onResume,
	onStop,
}: RecordingPanelProps) {
	/** Context */
	const { t } = useTranslation();

	/** Render */
	return (
		<div className="card v-stack gap-2 p-3 min-w-[15rem] shadow-lg">
			<div className="h-stack items-center gap-2">
				<StatusIndicator
					color={isPaused ? "gray" : "red"}
					size="md"
					withPing={!isPaused}
				/>
				<span className="font-medium tabular-nums">{elapsed}</span>
				<span className="label ml-auto">
					{t(isPaused ? "video_recorder.paused_short" : "video_recorder.recording_short")}
				</span>
				{/* A recording stops itself at the length limit. Saying so only once it is close
				    keeps the clock quiet for the tutorials that never approach it, and a stop
				    nobody saw coming is the one that reads as a crash. */}
				{remaining && (
					<span className="font-medium text-amber-600 text-xs tabular-nums dark:text-amber-400">
						{t("video_recorder.remaining", { time: remaining })}
					</span>
				)}
				{onUndock && (
					// Only offered in the page, and only by the author's own click: the window can
					// be opened solely while a gesture is fresh, and one that appeared at every
					// start would be in the way of everyone who never leaves the browser.
					<Button
						size="xs"
						variant="ghost"
						onClick={onUndock}
						aria-label={t("video_recorder.undock")}
						icon={<Icon name="PictureInPicture2" />}
					/>
				)}
			</div>

			{/* The emphasis follows the state, and it flips. While recording, ending it is the
			    escape and carries the weight. While paused, the author's next move is almost
			    always to carry on, and a loud red button would make the quiet state the loudest
			    thing on screen — which is the opposite of what paused has to communicate. */}
			<div className="h-stack gap-2">
				<Button
					size="xs"
					variant={isPaused ? "primary" : "white"}
					className="flex-1"
					onClick={isPaused ? onResume : onPause}
					icon={<Icon name={isPaused ? "Play" : "Pause"} />}
				>
					{t(isPaused ? "video_recorder.resume_recording" : "video_recorder.pause_recording")}
				</Button>
				<Button
					size="xs"
					variant={isPaused ? "danger-ghost" : "danger"}
					className="flex-1"
					onClick={onStop}
					icon={<Icon name="Square" />}
				>
					{t("video_recorder.stop_recording")}
				</Button>
			</div>
		</div>
	);
}
